import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Send,
  Clock,
  Pause,
  XCircle,
  Copy,
  Trash2,
  Edit2,
  Eye,
  BarChart3,
  Users,
  Mail,
  CheckCircle,
  ArrowLeft,
  Megaphone,
  Calendar,
  MousePointerClick,
  ChevronDown,
  FlaskConical,
  Loader2,
  UserPlus,
  UserX,
  FileText,
  Rocket,
  Gift,
  BookOpen,
  Shield,
  TrendingUp,
  Bell,
  Sparkles,
  Wand2,
} from "lucide-react";
import api from "@/lib/api-client";
import VisualEmailEditor from "@/components/VisualEmailEditor";
import EmailHtmlSizeNotice from "@/components/EmailHtmlSizeNotice";
import { wrapEmailContent, BRAND_COLORS } from "@/lib/email-layout";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import CampaignRecipientsTable, {
  type CampaignStatsRecipient,
  type RecipientStatusFilter,
} from "@/components/CampaignRecipientsTable";

/**
 * Convierte un ISO UTC (el que devuelve el backend en `scheduledAt`) al valor
 * que espera un `<input type="datetime-local">`, en la zona horaria LOCAL del
 * navegador. El propio input siempre interpreta/genera su valor en hora
 * local (nunca UTC): recortar el ISO en crudo (`iso.slice(0, 16)`) muestra la
 * hora UTC como si fuera local, y al guardar de nuevo (`new
 * Date(value).toISOString()`, que SÍ interpreta el valor como local) la
 * campaña se reprogramaba varias horas antes de lo previsto.
 */
function isoToLocalDatetimeInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ============================================================================
// TYPES
// ============================================================================

interface Campaign {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  audienceType: string;
  audienceFilters: AudienceFilters | null;
  status: string;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  openCount: number;
  clickCount: number;
  createdAt: string;
  _count?: { recipients: number };
}

interface AudienceFilters {
  planId?: string;
  subscriptionStatus?: string;
  customEmails?: string[];
}

interface AudiencePreview {
  total: number;
  preview: Array<{
    id: string;
    name: string;
    email: string;
    organizationName: string;
  }>;
}

interface CampaignStats {
  campaign: Campaign;
  byStatus: Record<string, number>;
  openRate: number;
  clickRate: number;
  /** Bajas atribuidas a ESTA campaña. Siempre 0 en campañas de plataforma: sus
   * destinatarios son User (no Customer) y su baja es por mailto:, no por
   * token/enlace propio de campaña. Se mantiene por coherencia de contrato con
   * `EmailCampaigns.tsx` (mismo helper compartido en el backend). */
  unsubscribedCount: number;
  /** Página actual de la tabla de destinatarios (paginada, con búsqueda/filtro). */
  recipients: CampaignStatsRecipient[];
  /** Total de destinatarios que casan el filtro/búsqueda (para paginar). */
  total: number;
  page: number;
  limit: number;
}

interface Plan {
  id: string;
  name: string;
}

/** Tamaño de página por defecto de la tabla de destinatarios (igual que el backend). */
const CAMPAIGN_STATS_DEFAULT_LIMIT = 25;

type ViewMode = "list" | "templates" | "editor" | "stats";

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_CONFIG: Record<
  string,
  { label: string; bg: string; text: string; icon: typeof Clock }
> = {
  draft: {
    label: "Borrador",
    bg: "bg-gray-100",
    text: "text-gray-700",
    icon: Edit2,
  },
  scheduled: {
    label: "Programada",
    bg: "bg-blue-100",
    text: "text-blue-700",
    icon: Clock,
  },
  sending: {
    label: "Enviando",
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    icon: Send,
  },
  sent: {
    label: "Enviada",
    bg: "bg-green-100",
    text: "text-green-700",
    icon: CheckCircle,
  },
  paused: {
    label: "Pausada",
    bg: "bg-orange-100",
    text: "text-orange-700",
    icon: Pause,
  },
  cancelled: {
    label: "Cancelada",
    bg: "bg-red-100",
    text: "text-red-700",
    icon: XCircle,
  },
};

const ADMIN_CAMPAIGN_TEMPLATES = [
  {
    id: "blank",
    name: "En blanco",
    description: "Empieza desde cero con una plantilla básica",
    icon: FileText,
    color: "bg-gray-100 text-gray-600",
    subject: "",
  },
  {
    id: "nueva_funcionalidad",
    name: "Nueva Funcionalidad",
    description: "Anuncia una nueva función o mejora de la plataforma",
    icon: Rocket,
    color: "bg-purple-100 text-purple-700",
    subject: "🚀 Nueva funcionalidad en ReservaManager",
  },
  {
    id: "oferta_plan",
    name: "Oferta / Promoción de Plan",
    description: "Ofrece descuentos o promociones en planes de suscripción",
    icon: Gift,
    color: "bg-red-100 text-red-700",
    subject: "🎁 Oferta especial para tu restaurante",
  },
  {
    id: "consejos",
    name: "Consejos / Tips",
    description: "Comparte consejos para sacar más partido a ReservaManager",
    icon: BookOpen,
    color: "bg-amber-100 text-amber-700",
    subject: "💡 Consejos para mejorar tus reservas",
  },
  {
    id: "mantenimiento",
    name: "Mantenimiento / Aviso",
    description: "Informa sobre mantenimiento programado o incidencias",
    icon: Shield,
    color: "bg-blue-100 text-blue-700",
    subject: "🔧 Aviso importante de ReservaManager",
  },
  {
    id: "caso_exito",
    name: "Caso de Éxito",
    description: "Comparte historias de éxito de otros hosteleros",
    icon: TrendingUp,
    color: "bg-green-100 text-green-700",
    subject: "📈 Descubre cómo otros hosteleros triunfan con ReservaManager",
  },
  {
    id: "recordatorio",
    name: "Recordatorio / Seguimiento",
    description: "Recuerda a hosteleros inactivos que vuelvan a la plataforma",
    icon: Bell,
    color: "bg-orange-100 text-orange-700",
    subject: "👋 Te echamos de menos en ReservaManager",
  },
  {
    id: "novedades_plataforma",
    name: "Novedades / Newsletter",
    description: "Resume las últimas mejoras y novedades de la plataforma",
    icon: Sparkles,
    color: "bg-indigo-100 text-indigo-700",
    subject: "✨ Novedades de ReservaManager",
  },
];

const AUDIENCE_TYPES = [
  {
    value: "all",
    label: "Todos los hosteleros",
    description: "Enviar a todos los hosteleros con email",
  },
  {
    value: "active",
    label: "Suscripción activa",
    description: "Solo hosteleros con suscripción activa",
  },
  {
    value: "trial",
    label: "En periodo de prueba",
    description: "Solo hosteleros en trial",
  },
  {
    value: "suspended",
    label: "Suspendidos",
    description: "Solo hosteleros con suscripción suspendida o cancelada",
  },
  {
    value: "plan",
    label: "Por plan",
    description: "Filtrar por plan específico",
  },
  {
    value: "custom",
    label: "Personalizado",
    description: "Enviar a emails específicos que tú elijas",
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export default function AdminCampaigns() {
  // View state
  const [view, setView] = useState<ViewMode>("list");
  const [editingId, setEditingId] = useState<string | null>(null);

  // List state
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  // Editor state
  const [form, setForm] = useState({
    name: "",
    subject: "",
    htmlContent: "",
    audienceType: "all",
    audienceFilters: {} as AudienceFilters,
    scheduledAt: "",
  });
  const [saving, setSaving] = useState(false);
  const [contentTab, setContentTab] = useState<"visual" | "html">("visual");
  const [audiencePreview, setAudiencePreview] =
    useState<AudiencePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [customEmailsText, setCustomEmailsText] = useState("");
  const [audienceStats, setAudienceStats] = useState<{
    all: number;
    active: number;
    trial: number;
    suspended: number;
    plans: { id: string; name: string; count: number }[];
  } | null>(null);

  // Test email
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  // AI Generator
  const [aiPrompt, setAiPrompt] = useState("");
  const [generatingAI, setGeneratingAI] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);

  // Stats state
  const [stats, setStats] = useState<CampaignStats | null>(null);
  // Campaña cuyas estadísticas se están viendo (distinto de `stats?.campaign.id`
  // porque tiene que existir ANTES de la primera respuesta, para poder disparar
  // el efecto de refetch en cuanto se entra en la pestaña).
  const [statsCampaignId, setStatsCampaignId] = useState<string | null>(null);
  // Tabla paginada de destinatarios: página/tamaño/búsqueda/filtro de la
  // pestaña de estadísticas. `recipientsSearchInput` es el valor tal cual del
  // buscador; `recipientsSearch` es la versión debounced que de verdad viaja
  // a la API (evita una petición por tecla).
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsLimit, setRecipientsLimit] = useState(
    CAMPAIGN_STATS_DEFAULT_LIMIT,
  );
  const [recipientsSearchInput, setRecipientsSearchInput] = useState("");
  const [recipientsSearch, setRecipientsSearch] = useState("");
  const [recipientsStatusFilter, setRecipientsStatusFilter] =
    useState<RecipientStatusFilter>("all");
  // Refetch en curso (carga inicial de la pestaña Y paginación/búsqueda/
  // filtro posteriores). Solo pinta un indicador discreto junto al título de
  // la tabla; la pantalla completa se gatea con `!stats` (ver `renderStats`).
  const [recipientsTableLoading, setRecipientsTableLoading] = useState(false);

  // General
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = { limit: "50" };
      if (filterStatus !== "all") params.status = filterStatus;
      const res = await api.get("/admin/campaigns", { params });
      setCampaigns(res.data.data);
    } catch {
      showToast("Error al cargar campañas", "error");
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Fetch plans and audience stats
  useEffect(() => {
    const fetchAudienceData = async () => {
      try {
        const res = await api.get("/admin/campaigns/audience-stats");
        const data = res.data.data;
        setAudienceStats(data);
        setPlans(
          data.plans.map((p: { id: string; name: string }) => ({
            id: p.id,
            name: p.name,
          })),
        );
      } catch {
        // Fallback: load plans separately
        try {
          const res = await api.get("/admin/plans");
          setPlans(res.data.data || res.data);
        } catch {
          /* ignore */
        }
      }
    };
    fetchAudienceData();
  }, []);

  /**
   * Trae el resumen + la página actual de destinatarios de una campaña. Los
   * parámetros de paginación/búsqueda/filtro son opcionales (por defecto,
   * página 1 sin búsqueda ni filtro) para que las llamadas "de refresco" tras
   * enviar/pausar puedan seguir invocándose con solo el id, igual que antes.
   *
   * Se puede entrar en la pestaña de estadísticas SOLO vía `handleViewStats`
   * (desde la lista, que siempre parte de `stats === null` gracias a
   * `handleBack`), así que la carga inicial y los refetch posteriores
   * (paginación/búsqueda/filtro) se distinguen con `!stats` en `renderStats`
   * sin necesidad de un booleano de carga aparte: `recipientsTableLoading`
   * solo pinta un indicador discreto junto al título, nunca sustituye la
   * tabla ya renderizada.
   */
  const fetchStats = useCallback(
    async (
      id: string,
      params?: {
        page?: number;
        limit?: number;
        search?: string;
        status?: RecipientStatusFilter;
      },
    ) => {
      try {
        setRecipientsTableLoading(true);
        const query = new URLSearchParams();
        query.set("page", String(params?.page ?? 1));
        query.set(
          "limit",
          String(params?.limit ?? CAMPAIGN_STATS_DEFAULT_LIMIT),
        );
        if (params?.search) query.set("search", params.search);
        if (params?.status && params.status !== "all") {
          query.set("status", params.status);
        }
        const res = await api.get(
          `/admin/campaigns/${id}/stats?${query.toString()}`,
        );
        setStats(res.data.data);
      } catch {
        showToast("Error al cargar estadísticas", "error");
      } finally {
        setRecipientsTableLoading(false);
      }
    },
    [showToast],
  );

  // Debounce del buscador de destinatarios (evita una petición por tecla) y
  // vuelta a la página 1 al cambiar el texto (mismo criterio que el filtro de
  // estado, ver el handler del <select>).
  useEffect(() => {
    const t = setTimeout(() => {
      setRecipientsSearch(recipientsSearchInput.trim());
      setRecipientsPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [recipientsSearchInput]);

  // Refetch de la tabla de destinatarios cuando cambia la página/tamaño/
  // búsqueda/filtro mientras se está viendo la pestaña de estadísticas de una
  // campaña concreta. No se dispara para otras pestañas ni si aún no hay
  // ninguna campaña seleccionada (p. ej. antes de `handleViewStats`).
  useEffect(() => {
    if (!statsCampaignId || view !== "stats") return;
    fetchStats(statsCampaignId, {
      page: recipientsPage,
      limit: recipientsLimit,
      search: recipientsSearch,
      status: recipientsStatusFilter,
    });
  }, [
    statsCampaignId,
    recipientsPage,
    recipientsLimit,
    recipientsSearch,
    recipientsStatusFilter,
    view,
    fetchStats,
  ]);

  const fetchAudiencePreview = async (campaignId: string) => {
    try {
      setLoadingPreview(true);
      const res = await api.post(
        `/admin/campaigns/${campaignId}/preview-audience`,
      );
      setAudiencePreview(res.data.data);
    } catch {
      setAudiencePreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const showMsg = (msg: string) => {
    showToast(msg, "success");
  };

  const handleCreate = () => {
    setEditingId(null);
    setView("templates");
  };

  const handleSelectTemplate = (templateId: string) => {
    const template = ADMIN_CAMPAIGN_TEMPLATES.find((t) => t.id === templateId);
    // Las plantillas solo aportan el FRAGMENTO de contenido: el envoltorio
    // (cabecera/pie de ReservaManager) es siempre el mismo, lo ponga la
    // plantilla o el generador de IA (ver `handleGenerateAI`) — un único
    // envoltorio visual, ver `apps/dashboard/src/lib/email-layout.ts`.
    const content =
      templateId === "blank"
        ? getDefaultTemplate()
        : getAdminCampaignTemplate(templateId);
    const htmlContent = wrapEmailContent({
      variant: "admin",
      brandName: "ReservaManager",
      content,
    });
    setForm({
      name: "",
      subject: template?.subject || "",
      htmlContent,
      audienceType: "all",
      audienceFilters: {},
      scheduledAt: "",
    });
    setCustomEmailsText("");
    setTestEmail("");
    setAudiencePreview(null);
    setContentTab("visual");
    setView("editor");
  };

  const handleEdit = (campaign: Campaign) => {
    setEditingId(campaign.id);
    const rawType = campaign.audienceType.replace("admin_", "");
    const filters = campaign.audienceFilters || {};
    setForm({
      name: campaign.name,
      subject: campaign.subject,
      htmlContent: campaign.htmlContent,
      audienceType: rawType,
      audienceFilters: filters,
      scheduledAt: campaign.scheduledAt
        ? isoToLocalDatetimeInputValue(campaign.scheduledAt)
        : "",
    });
    setCustomEmailsText((filters.customEmails || []).join("\n"));
    setTestEmail("");
    setAudiencePreview(null);
    setContentTab("visual");
    setView("editor");
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.htmlContent.trim()) {
      showToast("Nombre, asunto y contenido son obligatorios", "error");
      return;
    }

    try {
      setSaving(true);

      // Build audience filters, including custom emails if applicable
      const audienceFilters = { ...form.audienceFilters };
      if (form.audienceType === "custom") {
        const emails = customEmailsText
          .split(/[\n,;]+/)
          .map((e) => e.trim())
          .filter((e) => e && e.includes("@"));
        if (emails.length === 0) {
          showToast(
            "Debes añadir al menos un email válido para audiencia personalizada",
            "error",
          );
          setSaving(false);
          return;
        }
        audienceFilters.customEmails = emails;
      }

      const payload = {
        name: form.name.trim(),
        subject: form.subject.trim(),
        htmlContent: form.htmlContent,
        audienceType: form.audienceType,
        audienceFilters:
          Object.keys(audienceFilters).length > 0 ? audienceFilters : undefined,
        scheduledAt: form.scheduledAt
          ? new Date(form.scheduledAt).toISOString()
          : null,
      };

      let saved: { id: string; htmlContent?: string };
      if (editingId) {
        const res = await api.put(`/admin/campaigns/${editingId}`, payload);
        saved = res.data.data;
        showMsg("Campaña actualizada");
      } else {
        const res = await api.post("/admin/campaigns", payload);
        saved = res.data.data;
        setEditingId(saved.id);
        showMsg("Campaña creada");
      }

      // El backend devuelve el HTML ya saneado (imágenes en base64
      // sustituidas por su URL pública). Sin esto, el siguiente guardado
      // volvería a subir la misma imagen de nuevo.
      if (saved?.htmlContent && saved.htmlContent !== form.htmlContent) {
        const sanitizedHtml = saved.htmlContent;
        setForm((f) => ({ ...f, htmlContent: sanitizedHtml }));
      }

      await fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: "¿Eliminar esta campaña?", message: "Esta acción no se puede deshacer.", confirmText: "Sí, eliminar", danger: true }))) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      showMsg("Campaña eliminada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al eliminar", "error");
    }
  };

  const handleSend = async (id: string) => {
    if (
      !(await confirm({
        title: "¿Enviar esta campaña?",
        message: "Se enviará ahora a todos los hosteleros elegibles. Esta acción no se puede deshacer.",
        confirmText: "Sí, enviar",
      }))
    )
      return;
    try {
      const res = await api.post(`/admin/campaigns/${id}/send`);
      showMsg(res.data.message);
      fetchCampaigns();
      if (view === "stats") {
        fetchStats(id, {
          page: recipientsPage,
          limit: recipientsLimit,
          search: recipientsSearch,
          status: recipientsStatusFilter,
        });
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al enviar", "error");
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await api.post(`/admin/campaigns/${id}/duplicate`);
      showMsg("Campaña duplicada");
      fetchCampaigns();
    } catch (err: any) {
      showToast(err.response?.data?.message || "Error al duplicar", "error");
    }
  };

  const handleSendTest = async () => {
    if (!testEmail.trim() || !testEmail.includes("@")) {
      showToast("Introduce un email válido para la prueba", "error");
      return;
    }
    if (!editingId) {
      showToast(
        "Guarda la campaña primero antes de enviar una prueba",
        "error",
      );
      return;
    }
    try {
      setSendingTest(true);
      const res = await api.post(`/admin/campaigns/${editingId}/send-test`, {
        email: testEmail.trim(),
        subject: form.subject,
        htmlContent: form.htmlContent,
      });
      showMsg(res.data.message);
    } catch (err: any) {
      showToast(
        err.response?.data?.message || "Error al enviar prueba",
        "error",
      );
    } finally {
      setSendingTest(false);
    }
  };

  const handleViewStats = (campaign: Campaign) => {
    // Reinicia paginación/búsqueda/filtro de una vista de estadísticas anterior
    // (si la hubiera): el efecto de refetch se dispara solo por el cambio de
    // `statsCampaignId`, así que no hace falta llamar a `fetchStats` aquí.
    setRecipientsPage(1);
    setRecipientsSearchInput("");
    setRecipientsSearch("");
    setRecipientsStatusFilter("all");
    setStatsCampaignId(campaign.id);
    setView("stats");
  };

  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      showToast("Escribe qué quieres comunicar", "error");
      return;
    }
    try {
      setGeneratingAI(true);
      const res = await api.post("/admin/campaigns/generate-ai", {
        prompt: aiPrompt.trim(),
      });
      const { subject, body, html } = res.data.data;
      // El backend ya solo devuelve `{ subject, body }` (el fragmento de
      // contenido; el envoltorio lo pone siempre este módulo, igual que a
      // las plantillas). Rama de compatibilidad temporal mientras backend
      // (Railway) y dashboard (Vercel) se despliegan por separado: si el
      // backend desplegado todavía es el anterior, devuelve `html` ya
      // envuelto y se usa tal cual. Si ninguno de los dos llega como string
      // (ventana de despliegue en sentido contrario: dashboard ya
      // actualizado, backend aún sin desplegar), se conserva el contenido
      // previo del formulario en vez de vaciarlo — evita el TypeError de
      // `handleSave` (`form.htmlContent.trim()` sobre `undefined`) y la
      // pérdida silenciosa del borrador. Borrar esta rama cuando ambos
      // estén desplegados.
      const htmlContent =
        typeof body === "string"
          ? wrapEmailContent({
              variant: "admin",
              brandName: "ReservaManager",
              content: body,
            })
          : typeof html === "string"
            ? html
            : form.htmlContent;
      setForm({ ...form, subject: subject || form.subject, htmlContent });
      setShowAIGenerator(false);
      setAiPrompt("");
      showMsg("Email generado con IA correctamente");
    } catch (err: any) {
      showToast(
        err.response?.data?.message || "Error al generar con IA",
        "error",
      );
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleBack = () => {
    setView("list");
    setEditingId(null);
    setStats(null);
    setStatsCampaignId(null);
    setRecipientsPage(1);
    setRecipientsSearchInput("");
    setRecipientsSearch("");
    setRecipientsStatusFilter("all");
    setAudiencePreview(null);
  };

  // ============================================================================
  // RENDER: TEMPLATE SELECTOR
  // ============================================================================

  const renderTemplateSelector = () => (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="mb-4 md:mb-6 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Elige una plantilla
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Selecciona una plantilla para comenzar tu campaña a hosteleros
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {ADMIN_CAMPAIGN_TEMPLATES.map((tpl) => {
          const Icon = tpl.icon;
          return (
            <button
              key={tpl.id}
              onClick={() => handleSelectTemplate(tpl.id)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 text-left hover:shadow-md hover:border-primary-300 transition-all group"
            >
              <div
                className={`w-12 h-12 rounded-xl ${tpl.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}
              >
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{tpl.name}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                {tpl.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: LIST VIEW
  // ============================================================================

  const renderList = () => (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary-600" />
            Campañas a Hosteleros
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Envía comunicaciones masivas a los hosteleros de la plataforma
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Nueva Campaña
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2">
        {[
          { value: "all", label: "Todas" },
          { value: "draft", label: "Borradores" },
          { value: "scheduled", label: "Programadas" },
          { value: "sending", label: "Enviando" },
          { value: "sent", label: "Enviadas" },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setFilterStatus(f.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterStatus === f.value
                ? "bg-primary-100 text-primary-700"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Campaign List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            Cargando campañas...
          </div>
        ) : campaigns.length === 0 ? (
          <div className="p-12 text-center">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No hay campañas</p>
            <p className="text-gray-400 text-sm mt-1">
              Crea tu primera campaña para comunicarte con tus hosteleros
            </p>
            <button
              onClick={handleCreate}
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              Crear Campaña
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {campaigns.map((c) => {
              const sc = STATUS_CONFIG[c.status] || STATUS_CONFIG.draft;
              const StatusIcon = sc.icon;
              const displayAudience = c.audienceType.replace("admin_", "");
              const audienceLabel =
                AUDIENCE_TYPES.find((t) => t.value === displayAudience)
                  ?.label || displayAudience;
              return (
                <div
                  key={c.id}
                  className="p-4 md:px-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {c.name}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {sc.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">
                        <Mail className="w-3.5 h-3.5 inline mr-1" />
                        {c.subject}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(c.createdAt).toLocaleDateString("es-ES")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {audienceLabel}
                        </span>
                        {c.totalRecipients > 0 && (
                          <>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {c.totalRecipients} destinatarios
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {c.openCount} abiertos
                            </span>
                            <span className="flex items-center gap-1">
                              <MousePointerClick className="w-3 h-3" />
                              {c.clickCount} clics
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {["draft", "scheduled"].includes(c.status) && (
                        <>
                          <button
                            onClick={() => handleEdit(c)}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleSend(c.id)}
                            className="p-2 hover:bg-green-50 rounded-lg"
                            title="Enviar ahora"
                          >
                            <Send className="w-4 h-4 text-green-600" />
                          </button>
                        </>
                      )}
                      {["sent", "sending", "paused"].includes(c.status) && (
                        <button
                          onClick={() => handleViewStats(c)}
                          className="p-2 hover:bg-blue-50 rounded-lg"
                          title="Estadísticas"
                        >
                          <BarChart3 className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDuplicate(c.id)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title="Duplicar"
                      >
                        <Copy className="w-4 h-4 text-gray-500" />
                      </button>
                      {["draft", "scheduled", "cancelled"].includes(
                        c.status,
                      ) && (
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="p-2 hover:bg-red-50 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: EDITOR VIEW
  // ============================================================================

  const renderEditor = () => (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {editingId ? "Editar Campaña" : "Nueva Campaña"}
            </h1>
            <p className="text-gray-500 text-sm">
              Envío masivo a hosteleros de la plataforma
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editingId && (
            <button
              onClick={() => handleSend(editingId)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Enviar Ahora</span>
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Main content - 2/3 */}
        <div className="lg:col-span-2 space-y-4">
          {/* Basic Info */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">
              Información básica
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la campaña
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Novedades Abril 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asunto del email
                </label>
                <input
                  type="text"
                  value={form.subject}
                  onChange={(e) =>
                    setForm({ ...form, subject: e.target.value })
                  }
                  placeholder="Ej: Nuevas funcionalidades disponibles en ReservaManager"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Variables disponibles: {"{{customerName}}"}, {"{{email}}"}
                </p>
              </div>
            </div>
          </div>

          {/* AI Generator */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200 overflow-hidden">
            <button
              onClick={() => setShowAIGenerator(!showAIGenerator)}
              className="w-full flex items-center justify-between px-4 md:px-6 py-3 hover:bg-purple-50/50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-semibold text-purple-900">
                  Generar con IA
                </span>
                <span className="text-xs text-purple-500 hidden sm:inline">
                  — Describe lo que quieres y la IA creará el email
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-purple-400 transition-transform ${showAIGenerator ? "rotate-180" : ""}`}
              />
            </button>
            {showAIGenerator && (
              <div className="px-4 md:px-6 pb-4 space-y-3">
                <textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Ej: Quiero anunciar a los hosteleros que hemos lanzado una nueva funcionalidad de centralita IA que responde llamadas automáticamente..."
                  rows={3}
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-white placeholder:text-purple-300"
                />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-purple-400">
                    {aiPrompt.length}/1000 caracteres
                  </p>
                  <button
                    onClick={handleGenerateAI}
                    disabled={generatingAI || !aiPrompt.trim()}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingAI ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Generar email
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                Contenido del email
              </h2>
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setContentTab("visual")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${contentTab === "visual" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  Editor visual
                </button>
                <button
                  onClick={() => setContentTab("html")}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${contentTab === "html" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  HTML
                </button>
              </div>
            </div>
            <div className={contentTab === "html" ? "p-4 md:p-6" : ""}>
              {contentTab === "html" ? (
                <div className="space-y-3">
                  <textarea
                    value={form.htmlContent}
                    onChange={(e) =>
                      setForm({ ...form, htmlContent: e.target.value })
                    }
                    rows={18}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
                    placeholder="<html>...</html>"
                  />
                  {/* Preview */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">
                      Vista previa:
                    </p>
                    <div
                      className="border border-gray-200 rounded-lg overflow-hidden"
                      style={{ height: "300px" }}
                    >
                      <iframe
                        srcDoc={
                          form.htmlContent ||
                          "<p style='color:#999;padding:20px;font-family:sans-serif'>Sin contenido aún...</p>"
                        }
                        className="w-full h-full"
                        title="Vista previa"
                        sandbox=""
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <VisualEmailEditor
                  value={form.htmlContent}
                  onChange={(html) => setForm({ ...form, htmlContent: html })}
                  brandName="ReservaManager"
                  variant="admin"
                />
              )}
            </div>
            <div className="px-4 md:px-6 pb-4 md:pb-6">
              <EmailHtmlSizeNotice html={form.htmlContent} />
            </div>
          </div>
        </div>

        {/* Sidebar - 1/3 */}
        <div className="space-y-4">
          {/* Audience */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary-600" />
              Audiencia
            </h2>

            <div className="space-y-3">
              {/* Segment selector as cards */}
              <div className="space-y-2">
                {[
                  {
                    value: "all",
                    label: "Todos los hosteleros",
                    icon: "👥",
                    count: audienceStats?.all,
                  },
                  {
                    value: "active",
                    label: "Suscripción activa",
                    icon: "✅",
                    count: audienceStats?.active,
                  },
                  {
                    value: "trial",
                    label: "En periodo de prueba",
                    icon: "⏳",
                    count: audienceStats?.trial,
                  },
                  {
                    value: "suspended",
                    label: "Suspendidos",
                    icon: "⛔",
                    count: audienceStats?.suspended,
                  },
                  { value: "plan", label: "Por plan", icon: "📋", count: null },
                  {
                    value: "custom",
                    label: "Personalizado",
                    icon: "✉️",
                    count: null,
                  },
                ].map((seg) => (
                  <button
                    key={seg.value}
                    onClick={() =>
                      setForm({
                        ...form,
                        audienceType: seg.value,
                        audienceFilters: {},
                      })
                    }
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm transition-colors ${
                      form.audienceType === seg.value
                        ? "bg-primary-50 border-2 border-primary-500 text-primary-800 font-medium"
                        : "bg-gray-50 border-2 border-transparent hover:border-gray-200 text-gray-700"
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">{seg.icon}</span>
                    <span className="flex-1">{seg.label}</span>
                    {seg.count != null && (
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          form.audienceType === seg.value
                            ? "bg-primary-100 text-primary-700"
                            : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {seg.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Plan sub-selector */}
              {form.audienceType === "plan" && (
                <div className="space-y-2 pt-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Selecciona un plan
                  </label>
                  {audienceStats?.plans && audienceStats.plans.length > 0 ? (
                    <div className="space-y-1.5">
                      {audienceStats.plans.map((p) => (
                        <button
                          key={p.id}
                          onClick={() =>
                            setForm({
                              ...form,
                              audienceFilters: {
                                ...form.audienceFilters,
                                planId: p.id,
                              },
                            })
                          }
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                            form.audienceFilters.planId === p.id
                              ? "bg-indigo-50 border-2 border-indigo-500 text-indigo-800 font-medium"
                              : "bg-gray-50 border-2 border-transparent hover:border-gray-200 text-gray-700"
                          }`}
                        >
                          <span>{p.name}</span>
                          <span
                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              form.audienceFilters.planId === p.id
                                ? "bg-indigo-100 text-indigo-700"
                                : "bg-gray-200 text-gray-600"
                            }`}
                          >
                            {p.count} hosteleros
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="relative">
                      <select
                        value={form.audienceFilters.planId || ""}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            audienceFilters: {
                              ...form.audienceFilters,
                              planId: e.target.value,
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm appearance-none bg-white pr-8"
                      >
                        <option value="">Selecciona un plan</option>
                        {plans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  )}
                </div>
              )}

              {/* Custom emails */}
              {form.audienceType === "custom" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <UserPlus className="w-3.5 h-3.5 inline mr-1" />
                    Emails destinatarios
                  </label>
                  <textarea
                    value={customEmailsText}
                    onChange={(e) => setCustomEmailsText(e.target.value)}
                    rows={5}
                    placeholder={
                      "usuario1@email.com\nusuario2@email.com\nusuario3@email.com"
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Un email por línea. También puedes separarlos con comas.
                    {customEmailsText && (
                      <span className="block mt-0.5 text-primary-600 font-medium">
                        {
                          customEmailsText
                            .split(/[\n,;]+/)
                            .filter((e) => e.trim() && e.trim().includes("@"))
                            .length
                        }{" "}
                        emails válidos
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Preview button */}
              {editingId && (
                <button
                  onClick={() => fetchAudiencePreview(editingId)}
                  disabled={loadingPreview}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                >
                  <Users className="w-4 h-4" />
                  {loadingPreview ? "Calculando..." : "Ver audiencia estimada"}
                </button>
              )}

              {audiencePreview && (
                <div className="bg-blue-50 rounded-lg p-3 text-sm">
                  <div className="flex items-center gap-2 font-semibold text-blue-800 mb-2">
                    <Users className="w-4 h-4" />
                    Hosteleros destinatarios
                  </div>
                  <p className="text-blue-700 text-xs">
                    Total: <strong>{audiencePreview.total}</strong> hosteleros
                  </p>
                  {audiencePreview.preview.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {audiencePreview.preview.slice(0, 5).map((u) => (
                        <p
                          key={u.id}
                          className="text-xs text-blue-600 truncate"
                        >
                          {u.name} — {u.email}
                        </p>
                      ))}
                      {audiencePreview.total > 5 && (
                        <p className="text-xs text-blue-500">
                          y {audiencePreview.total - 5} más...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary-600" />
              Programación
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha y hora de envío
                </label>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) =>
                    setForm({ ...form, scheduledAt: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Deja vacío para enviar manualmente
                </p>
              </div>
              {form.scheduledAt && (
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>
                    Se enviará el{" "}
                    {new Date(form.scheduledAt).toLocaleString("es-ES", {
                      dateStyle: "long",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Help */}
          <div className="bg-primary-50 rounded-xl border border-primary-100 p-4">
            <h3 className="text-sm font-semibold text-primary-800 mb-2">
              Información
            </h3>
            <ul className="text-xs text-primary-700 space-y-1.5">
              <li>• Se envía usando el SMTP de la plataforma (Mailrelay)</li>
              <li>
                • Los destinatarios son hosteleros (usuarios admin) de las
                organizaciones
              </li>
              <li>
                • Usa {"{{customerName}}"} para personalizar con el nombre del
                hostelero
              </li>
              <li>• El tracking de aperturas y clics es automático</li>
            </ul>
          </div>

          {/* Send Test */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-orange-500" />
              Enviar prueba
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email de prueba
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !editingId}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                {sendingTest ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FlaskConical className="w-4 h-4" />
                )}
                {sendingTest ? "Enviando..." : "Enviar email de prueba"}
              </button>
              {!editingId && (
                <p className="text-xs text-gray-400">
                  Guarda la campaña primero para enviar una prueba
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: STATS VIEW
  // ============================================================================

  const renderStats = () => {
    if (!stats) {
      return (
        <div className="min-h-screen bg-gray-50 p-3 md:p-6">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              Cargando estadísticas...
            </h1>
          </div>
        </div>
      );
    }

    const { campaign, byStatus, openRate, clickRate, unsubscribedCount } =
      stats;

    return (
      <div className="min-h-screen bg-gray-50 p-3 md:p-6">
        {/* Header */}
        <div className="mb-4 md:mb-6 flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              {campaign.name}
            </h1>
            <p className="text-gray-500 text-sm">{campaign.subject}</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Users className="w-4 h-4" />
              Destinatarios
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {campaign.totalRecipients}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Send className="w-4 h-4" />
              Enviados
            </div>
            <p className="text-2xl font-bold text-green-600">
              {campaign.sentCount}
            </p>
            {campaign.failedCount > 0 && (
              <p className="text-xs text-red-500 mt-0.5">
                {campaign.failedCount} fallidos
              </p>
            )}
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Eye className="w-4 h-4" />
              Tasa de apertura
            </div>
            <p className="text-2xl font-bold text-blue-600">{openRate}%</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {(byStatus.opened || 0) + (byStatus.clicked || 0)} abiertos
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <MousePointerClick className="w-4 h-4" />
              Tasa de clics
            </div>
            <p className="text-2xl font-bold text-purple-600">{clickRate}%</p>
            <p className="text-xs text-gray-400 mt-0.5">
              {byStatus.clicked || 0} clics
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <UserX className="w-4 h-4" />
              Bajas
            </div>
            <p className="text-2xl font-bold text-red-600">
              {unsubscribedCount}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        {campaign.status === "sending" && campaign.totalRecipients > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progreso de envío
              </span>
              <span className="text-sm text-gray-500">
                {campaign.sentCount + campaign.failedCount} /{" "}
                {campaign.totalRecipients}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-primary-600 h-3 rounded-full transition-all"
                style={{
                  width: `${((campaign.sentCount + campaign.failedCount) / campaign.totalRecipients) * 100}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Recipients Table */}
        <CampaignRecipientsTable
          recipients={stats.recipients}
          total={stats.total}
          page={stats.page}
          limit={stats.limit}
          onPageChange={setRecipientsPage}
          onLimitChange={(l) => {
            setRecipientsLimit(l);
            setRecipientsPage(1);
          }}
          searchValue={recipientsSearchInput}
          onSearchValueChange={setRecipientsSearchInput}
          status={recipientsStatusFilter}
          onStatusChange={(s) => {
            setRecipientsStatusFilter(s);
            setRecipientsPage(1);
          }}
          loading={recipientsTableLoading}
          recipientLabel="Hostelero"
        />
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
      {/* Notifications moved to global toast */}

      {view === "list" && renderList()}
      {view === "templates" && renderTemplateSelector()}
      {view === "editor" && renderEditor()}
      {view === "stats" && renderStats()}
    </>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Plantillas predefinidas: devuelven SOLO el fragmento de contenido (nunca un
 * documento completo). El envoltorio —cabecera/pie de ReservaManager, sin
 * {{unsubscribeUrl}} (esas campañas van de la plataforma a hosteleros, no
 * llevan baja de cliente)— lo pone siempre `wrapEmailContent()` en
 * `handleSelectTemplate`, el MISMO módulo que envuelve lo que genera la IA.
 */
function getDefaultTemplate(): string {
  return `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>
<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Escribe aquí el contenido de tu comunicación...</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Ver más</a>
</td></tr>
</table>`;
}

function getAdminCampaignTemplate(templateId: string): string {
  const templates: Record<string, string> = {
    nueva_funcionalidad: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">🚀 Nueva Funcionalidad Disponible</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Nos complace anunciarte una nueva mejora en ReservaManager:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 8px;color:#111111;font-size:16px;font-weight:bold;">[NOMBRE DE LA FUNCIONALIDAD]</p>
<p style="margin:0 0 12px;color:#333333;font-size:14px;line-height:20px;">[Descripción breve de qué hace y cómo beneficia al hostelero]</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ [Beneficio 1]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ [Beneficio 2]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ [Beneficio 3]</td></tr>
</table>
</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Ya está disponible en tu panel de control. ¡Pruébala ahora!</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Ir al Dashboard</a>
</td></tr>
</table>`,

    oferta_plan: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">🎁 Oferta Especial para Tu Restaurante</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Tenemos una promoción exclusiva para ti:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" align="center" style="padding:20px;border-left:3px solid ${BRAND_COLORS.primaryDark};">
<p style="margin:0;color:${BRAND_COLORS.primaryDark};font-size:32px;font-weight:bold;">[X]% DESCUENTO</p>
<p style="margin:4px 0;color:#111111;font-size:16px;">en el plan [NOMBRE DEL PLAN]</p>
<p style="margin:8px 0 0;color:#555555;font-size:13px;">Válido hasta el <strong style="color:#111111;">[FECHA]</strong></p>
</td></tr>
</table>

<p style="margin:0 0 8px;color:#111111;font-size:15px;font-weight:bold;">¿Qué incluye?</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">📊 [Característica 1 del plan]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">📱 [Característica 2 del plan]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🤖 [Característica 3 del plan]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">📧 [Característica 4 del plan]</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">No dejes pasar esta oportunidad de llevar tu restaurante al siguiente nivel.</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com/subscription" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Ver Planes</a>
</td></tr>
</table>`,

    consejos: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">💡 Consejos para Mejorar tus Reservas</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Aquí tienes algunos tips para sacar el máximo partido a ReservaManager:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:12px 0;border-bottom:1px solid #e5e5e5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">🪑</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">Configura tus horarios correctamente</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">Asegúrate de que tus horarios de apertura están actualizados. Los clientes solo podrán reservar en los horarios que definas.</p>
</td>
</tr></table>
</td></tr>
<tr><td style="padding:12px 0;border-bottom:1px solid #e5e5e5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">📅</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">Usa el mapa de mesas</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">Diseña la distribución de tu restaurante para que las reservas se asignen automáticamente a las mesas más adecuadas.</p>
</td>
</tr></table>
</td></tr>
<tr><td style="padding:12px 0;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">🔔</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">Activa las notificaciones</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">Activa los recordatorios automáticos para reducir los no-shows hasta un 40%.</p>
</td>
</tr></table>
</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">¿Necesitas ayuda? Estamos aquí para lo que necesites.</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Ir al Dashboard</a>
</td></tr>
</table>`,

    mantenimiento: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">🔧 Aviso Importante</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Queremos informarte sobre un mantenimiento programado:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 6px;color:#333333;font-size:14px;"><strong style="color:#111111;">📅 Fecha:</strong> [FECHA]</p>
<p style="margin:0 0 6px;color:#333333;font-size:14px;"><strong style="color:#111111;">🕐 Horario:</strong> [HORA INICIO] - [HORA FIN]</p>
<p style="margin:0;color:#333333;font-size:14px;"><strong style="color:#111111;">⏱️ Duración estimada:</strong> [DURACIÓN]</p>
</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Durante este periodo, el servicio podría experimentar interrupciones breves. Estamos trabajando para mejorar la estabilidad y rendimiento de la plataforma.</p>

<p style="margin:0 0 8px;color:#111111;font-size:15px;font-weight:bold;">¿Qué debes hacer?</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ No es necesaria ninguna acción por tu parte</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ Las reservas existentes no se verán afectadas</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">✅ El widget de reservas seguirá funcionando normalmente</td></tr>
</table>

<p style="margin:0;color:#333333;font-size:15px;line-height:24px;">Disculpa las molestias. Gracias por tu comprensión.</p>`,

    caso_exito: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">📈 Caso de Éxito: [Nombre del Restaurante]</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Descubre cómo este restaurante transformó su gestión de reservas:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<p style="margin:0 0 6px;color:#111111;font-size:15px;font-weight:bold;font-style:italic;">"[Cita del hostelero sobre su experiencia con ReservaManager]"</p>
<p style="margin:0;color:#555555;font-size:13px;">— [Nombre], propietario de [Restaurante]</p>
</td></tr>
</table>

<p style="margin:0 0 8px;color:#111111;font-size:15px;font-weight:bold;">Resultados tras usar ReservaManager:</p>
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">📊 <strong style="color:#111111;">+[X]%</strong> de reservas online</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">⬇️ <strong style="color:#111111;">-[X]%</strong> de no-shows</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">⏰ <strong style="color:#111111;">[X] horas/semana</strong> ahorradas en gestión</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">⭐ <strong style="color:#111111;">[X]</strong> valoración media de clientes</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">¿Quieres resultados similares? Optimiza tu configuración ahora.</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Optimizar mi Restaurante</a>
</td></tr>
</table>`,

    recordatorio: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">👋 ¡Te Echamos de Menos!</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hace tiempo que no accedes a tu panel de ReservaManager. Mientras no estabas, hemos añadido mejoras que te van a encantar:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td bgcolor="#f4f4f5" style="padding:16px;border-left:3px solid ${BRAND_COLORS.primaryDark};font-size:15px;line-height:22px;color:#333333;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0">
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🆕 [Mejora reciente 1]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🆕 [Mejora reciente 2]</td></tr>
<tr><td style="padding:6px 0;color:#333333;font-size:14px;">🆕 [Mejora reciente 3]</td></tr>
</table>
</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Tu restaurante tiene <strong style="color:#111111;">[X] reservas pendientes</strong> por gestionar. No pierdas oportunidades de negocio.</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Volver al Dashboard</a>
</td></tr>
</table>

<p style="margin:16px 0 0;color:#555555;font-size:13px;text-align:center;">¿Necesitas ayuda? Escríbenos a <a href="mailto:reservamanager@lucrapp.com" style="color:${BRAND_COLORS.primaryDark};text-decoration:underline;">reservamanager@lucrapp.com</a></p>`,

    novedades_plataforma: `<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Hola <strong style="color:#111111;">{{customerName}}</strong>,</p>

<h2 style="margin:0 0 16px;color:#111111;font-size:20px;font-weight:bold;">✨ Novedades de ReservaManager</h2>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Estas son las últimas mejoras que hemos implementado:</p>

<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" style="margin:0 0 16px;">
<tr><td style="padding:12px 0;border-bottom:1px solid #e5e5e5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">🚀</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">[Novedad 1]</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">[Descripción breve de la novedad 1]</p>
</td>
</tr></table>
</td></tr>
<tr><td style="padding:12px 0;border-bottom:1px solid #e5e5e5;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">⚡</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">[Novedad 2]</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">[Descripción breve de la novedad 2]</p>
</td>
</tr></table>
</td></tr>
<tr><td style="padding:12px 0;">
<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0"><tr>
<td width="32" valign="top" style="padding-right:12px;font-size:22px;">🎨</td>
<td valign="top">
<p style="margin:0 0 4px;color:#111111;font-size:15px;font-weight:bold;">[Novedad 3]</p>
<p style="margin:0;color:#555555;font-size:14px;line-height:20px;">[Descripción breve de la novedad 3]</p>
</td>
</tr></table>
</td></tr>
</table>

<p style="margin:0 0 16px;color:#333333;font-size:15px;line-height:24px;">Estamos trabajando continuamente para mejorar tu experiencia. ¡Prueba las novedades ahora!</p>

<table role="presentation" border="0" cellpadding="0" cellspacing="0" align="center" style="margin:24px auto;">
<tr><td bgcolor="${BRAND_COLORS.primaryDark}" align="center" style="padding:14px 28px;">
<a href="https://dashboard.reservamanager.com" style="display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:#ffffff;text-decoration:none;">Descubrir Novedades</a>
</td></tr>
</table>`,
  };

  return templates[templateId] || getDefaultTemplate();
}
